'use client';

import { useWorkspacePermissions } from '@frameio/permissions';
import {
  useCloseContextMenu,
  useContextMenuIsOpen,
  useContextMenuPosition,
  useContextMenuTarget,
} from '@frameio/shared/contexts';
import { useIsIntegrationClient } from '@frameio/shared/contexts/ClientNameContext';
import { consumeEvent } from '@frameio/utils';
import {
  Menu,
  MenuButton,
  MenuGroup,
  MenuNonInteractiveArea,
  MenuSwitch,
  Separator,
  Text,
  useId,
} from '@frameio/vapor';
import {
  IconCog,
  IconInfo,
  IconNotifications,
  IconTrash,
} from '@frameio/vapor-icons';
import {
  useDeleteWorkspaceWithWarning,
  useOpenWorkspaceSettings,
  useShowWorkspaceNotificationsWarningModal,
} from '../../../../../settings';
import { usePaginatedWorkspaces } from '../../../../../shared';
import type { WorkspaceProjectTarget } from '../../../../hooks';
import { useWorkspaceContextMenuInfoFragment } from '../../gql/generated/WorkspaceContextMenuInfo';
import { ContextMenuForIntegrations } from './ContextMenuForIntegrations';
import TEXT from './generated/TEXT/ContextMenu_ContextMenu';

const MENU_WIDTH = '256px';

type Props = {
  onClick?: () => void;
};

export const ContextMenu = ({ onClick }: Props) => {
  const isIntegration = useIsIntegrationClient();

  const target = useContextMenuTarget<WorkspaceProjectTarget>();
  const isOpen = useContextMenuIsOpen() && target?.type === 'workspace';
  const workspaceId = target?.id ?? '';

  const position = useContextMenuPosition();
  const closeContextMenu = useCloseContextMenu();

  const { items: workspaces } = usePaginatedWorkspaces({
    fetchPolicy: 'cache-only',
  });
  const { data: workspace, complete } =
    useWorkspaceContextMenuInfoFragment(workspaceId);

  const canDelete = useWorkspacePermissions({
    check: 'canDelete',
    workspaceId,
  });
  const canUpdate = useWorkspacePermissions({
    check: 'canUpdate',
    workspaceId,
  });
  const hasProjectNotificationsEnabled = Boolean(
    workspace?.projectNotificationsEnabled
  );

  const deleteWorkspace = useDeleteWorkspaceWithWarning();
  // We can't delete the last workspace
  const canDeleteWorkspace = workspaces.length > 1;
  const showWorkspaceNotificationsWarningModal =
    useShowWorkspaceNotificationsWarningModal();
  const handleProjectNotificationsToggle = () => {
    onClick?.();
    showWorkspaceNotificationsWarningModal(workspaceId);
  };
  const openWorkspaceSettings = useOpenWorkspaceSettings();

  const handleDelete = () => {
    if (!workspace || !complete) return;
    onClick?.();
    deleteWorkspace({
      __typename: workspace.__typename,
      id: workspace.id,
      name: workspace.name,
    });
  };

  const handleSettings = () => {
    onClick?.();
    openWorkspaceSettings({ workspaceId });
  };

  // if delete is disabled, we want to provide a descriptive aria id
  const descriptiveAriaId = useId();
  const resolvedDescriptiveAriaId = !canDeleteWorkspace
    ? descriptiveAriaId
    : undefined;

  const menuProps = {
    isShowing: isOpen,
    anchorPosition: position,
    minWidth: MENU_WIDTH,
    onRequestChange: (isShowing: boolean) => {
      if (!isShowing) {
        closeContextMenu();
      }
    },
  };

  if (isIntegration) {
    return (
      <ContextMenuForIntegrations
        ariaId={resolvedDescriptiveAriaId}
        {...menuProps}
      />
    );
  }

  return (
    <Menu
      aria-describedby={resolvedDescriptiveAriaId}
      aria-label={TEXT.workspacesNavigationContextMenuAriaLabel}
      aria-labelledby={resolvedDescriptiveAriaId}
      {...menuProps}
      content={
        <div onClick={consumeEvent}>
          <MenuGroup label={TEXT.menuTitle}>
            <MenuSwitch
              checked={hasProjectNotificationsEnabled}
              iconBefore={<IconNotifications />}
              name={TEXT.projectNotifications}
              onPress={handleProjectNotificationsToggle}
              shouldCloseMenuOnSelect={true}
            >
              {TEXT.projectNotifications}
            </MenuSwitch>
            <MenuButton
              data-testid="workspace-overflow-menu-settings-button"
              disabled={!canUpdate}
              iconBefore={<IconCog />}
              onPress={handleSettings}
            >
              {TEXT.workspaceSettings}
            </MenuButton>
          </MenuGroup>

          <MenuGroup>
            <MenuButton
              disabled={!canDelete || !canDeleteWorkspace}
              iconBefore={<IconTrash />}
              onPress={handleDelete}
              variant="destructive"
            >
              {TEXT.delete}
            </MenuButton>
          </MenuGroup>

          {!canDeleteWorkspace && (
            <>
              <Separator color="secondary" />
              <MenuNonInteractiveArea icon={<IconInfo />}>
                <Text
                  color="tertiary"
                  id={resolvedDescriptiveAriaId}
                  variant="caption"
                >
                  {TEXT.workspaceRequired}
                </Text>
              </MenuNonInteractiveArea>
            </>
          )}
        </div>
      }
      hasRefocusEventOnClose={false}
    />
  );
};

TEXT.DEFINE({
  /** Menu item label to delete a workspace */
  delete: 'Delete',
  /** Menu group title for workspace actions */
  menuTitle: 'Workspace',
  /** Menu item label for toggling project notifications */
  projectNotifications: 'Project Notifications',
  /** Menu item label to open workspace settings */
  workspaceSettings: 'Workspace Settings',
  /** Disabled state message when workspace deletion is unavailable */
  workspaceRequired: 'You must have at least one workspace',
  /** Aria label for the workspaces navigation context menu */
  workspacesNavigationContextMenuAriaLabel:
    'Workspaces Navigation Context Menu',
});
